import { ColScaffold, RowScaffold, ScaffoldSelection } from "../../interface";
import { buildCellRange } from "./buildCellRange";

/**
 * Adds a rectangular cell range to the existing selection without removing other selections.
 *
 * @param selection - The current scaffold selection state.
 * @param rows - All available scaffold rows.
 * @param cols - All available scaffold columns.
 * @param anchorRowId - The row ID of the selection anchor.
 * @param anchorColId - The column ID of the selection anchor.
 * @param targetRowId - The row ID of the selection target.
 * @param targetColId - The column ID of the selection target.
 * @returns A new selection state with the cell range merged in.
 */
export const addCellRangeToSelection = <T>(
  selection: ScaffoldSelection,
  rows: RowScaffold<T>[],
  cols: ColScaffold<T>[],
  anchorRowId: string,
  anchorColId: string,
  targetRowId: string,
  targetColId: string,
): ScaffoldSelection => {
  const rangeItems = buildCellRange(rows, cols, anchorRowId, anchorColId, targetRowId, targetColId);

  const colIdsByRow = new Map<string, Set<string>>();

  for (const item of selection.selectedCells) {
    colIdsByRow.set(item.rowId, new Set(item.colId));
  }

  for (const rangeRow of rangeItems) {
    const existing = colIdsByRow.get(rangeRow.rowId);
    if (existing) {
      rangeRow.colId.forEach((c) => existing.add(c));
    } else {
      colIdsByRow.set(rangeRow.rowId, new Set(rangeRow.colId));
    }
  }

  const merged = Array.from(colIdsByRow.entries()).map(([rowId, colIdSet]) => ({
    rowId,
    colId: Array.from(colIdSet),
  }));

  return { ...selection, selectedCells: merged };
};
